import { Seo } from "../components/Seo";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Mail, 
  MessageSquare, 
  Building2, 
  Users, 
  ChevronRight,
  CheckCircle2,
  MapPin,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const inquiryTypes = [
  { value: "demo", label: "Request a Demo" },
  { value: "sales", label: "Sales Inquiry" },
  { value: "partnership", label: "Partnership" },
  { value: "support", label: "Technical Support" },
  { value: "careers", label: "Careers" },
  { value: "other", label: "Other" }
];

const contactInfo = [
  {
    icon: Mail,
    title: "Email",
    value: "Use the form below",
    href: "#contact-form"
  },
  {
    icon: Building2,
    title: "Headquarters",
    value: "Global TopCo — formation in progress",
    href: null
  },
  {
    icon: Clock,
    title: "Response Time",
    value: "Within 24 hours",
    href: null
  }
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    inquiryType: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, inquiryType: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Message sent successfully!");
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      <Seo title="Contact Sales & Request a Demo | SwarmCitadel" description="Talk to SwarmCitadel about governed AI agent deployments. Request a demo, discuss enterprise terms or explore partnerships." path="/contact" />
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-swarm-cyan/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="section-label">Contact</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              Let's Build
              <br />
              <span className="text-gradient inline-block">Together</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Whether you're interested in a demo, partnership, or just want to learn more, 
              we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div id="contact-form">
              {isSubmitted ? (
                <div className="glass-card p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-swarm-green/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-swarm-green" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Message Sent!</h3>
                  <p className="text-slate-400 mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: "", email: "", company: "", inquiryType: "", message: "" });
                    }}
                    variant="outline"
                    className="btn-outline"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-swarm-darker border-swarm-border text-white h-12"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@company.com"
                        className="bg-swarm-darker border-swarm-border text-white h-12"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Company</label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        className="bg-swarm-darker border-swarm-border text-white h-12"
                        data-testid="contact-company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Inquiry Type *</label>
                      <Select onValueChange={handleSelectChange} required>
                        <SelectTrigger className="bg-swarm-darker border-swarm-border text-white h-12" data-testid="contact-inquiry-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-swarm-darker border-swarm-border">
                          {inquiryTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your project or inquiry..."
                      rows={6}
                      className="bg-swarm-darker border-swarm-border text-white resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-glow w-full h-12"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message <ChevronRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <div className="glass-card p-8 mb-8">
                <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {contactInfo.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-swarm-violet/10">
                          <Icon className="w-5 h-5 text-swarm-violet" />
                        </div>
                        <div>
                          <div className="text-slate-500 text-sm">{item.title}</div>
                          {item.href ? (
                            <a href={item.href} className="text-white hover:text-swarm-cyan transition-colors">
                              {item.value}
                            </a>
                          ) : (
                            <div className="text-white">{item.value}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Links */}
              <div className="glass-card p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Quick Actions</h3>
                <div className="space-y-4">
                  <a
                    href="#contact-form"
                    className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-swarm-green/10">
                      <Users className="w-5 h-5 text-swarm-green" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">Investor Relations</div>
                      <div className="text-slate-500 text-sm">Use the contact form</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-swarm-cyan" />
                  </a>

                  <a
                    href="#contact-form"
                    className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-swarm-cyan/10">
                      <MessageSquare className="w-5 h-5 text-swarm-cyan" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">Technical Support</div>
                      <div className="text-slate-500 text-sm">Use the contact form</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-swarm-cyan" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
